import { StateCreator } from 'zustand'
import { calculateInvoiceTotals, generateNextInvoiceNumber, ensureInvoiceId, isSameCompany } from '../../utils/invoiceCalculations'
import { invoiceApi, userApi } from '../../lib/api'
import { useAuthStore } from '../authStore'
import { sampleInvoice } from '../../constants/invoice'
import { createPaymentMethodsFromSettings } from '../../utils/paymentMethodUtils'
import type { InvoiceStore } from '../invoiceStore'
import type { Sender } from '@dutchinvoice/types'
import type { SyncedInvoice } from '../../types/invoice'
import { invoiceHasContent } from '../selectors/invoiceSelectors'

export interface InvoiceManagementSlice {
    saveInvoice: () => void
    resetCurrentInvoice: () => void
    loadInvoice: (invoiceNumber: string) => void
    deleteInvoice: (invoiceNumber: string) => void
    copyInvoice: (invoiceNumber: string) => void
    getNextInvoiceNumber: () => string
    saveAsDefaultCompany: () => void
    isCurrentCompanySaved: () => boolean
    newInvoice: (sender?: Sender) => SyncedInvoice
    refreshSettings: () => Promise<void>
}

export const createInvoiceManagementSlice: StateCreator<
    InvoiceStore,
    [],
    [],
    InvoiceManagementSlice
> = (set, get) => ({
    // Create a new invoice with default settings
    newInvoice: (sender) => {
        const state = get();

        // Use provided sender or default company details if available, otherwise use sample invoice sender
        const invoiceSender = sender || (state.settings?.defaultCompany
            ? { ...state.settings.defaultCompany, isPlaceholder: undefined }
            : sampleInvoice.sender);

        // Get payment methods from settings
        const paymentMethods = state.settings
            ? createPaymentMethodsFromSettings(state.settings)
            : undefined;

        // Use default notes from settings if available
        const notes = state.settings?.defaultNotes || sampleInvoice.notes;
        const isNotesPlaceholder = !state.settings?.defaultNotes;

        return ensureInvoiceId({
            ...sampleInvoice,
            sender: invoiceSender,
            paymentMethods,
            notes,
            isPlaceholder: {
                ...sampleInvoice.isPlaceholder,
                paymentMethods: !paymentMethods,
                notes: isNotesPlaceholder
            },
            status: 'concept'
        }) as SyncedInvoice;
    },

    // Refresh settings from the backend
    refreshSettings: async () => {
        if (useAuthStore.getState().isAuthenticated) {
            try {
                const latestSettings = await userApi.getSettings();
                if (latestSettings) {
                    set({ settings: latestSettings });
                    console.log('Settings refreshed from server');
                }
            } catch (error) {
                console.error('Failed to fetch latest settings:', error);
            }
        }
    },

    saveInvoice: () =>
        set((state) => {
            try {
                // Extract scheduleData before modifying the invoice
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const { scheduleData, ...invoiceWithoutScheduleData } = state.currentInvoice as any;

                // Dates are already in YYYY-MM-DD format
                const currentInvoice = {
                    ...ensureInvoiceId({
                        ...invoiceWithoutScheduleData,
                        status: 'saved' as const
                    }) as SyncedInvoice,
                    // Add synced property after ensureInvoiceId to avoid type errors
                    synced: useAuthStore.getState().isAuthenticated // Mark as synced only if authenticated
                }

                // Generate invoice number if it doesn't exist
                if (!currentInvoice.invoiceNumber) {
                    currentInvoice.invoiceNumber = generateNextInvoiceNumber(state.settings)
                    // Immediately update local settings to prevent duplicate numbers
                    state.settings.latestInvoiceNumber = (state.settings.latestInvoiceNumber || 0) + 1
                }

                // Filter out invoices with the same invoice number
                const filteredInvoices = state.savedInvoices.filter(inv =>
                    inv.invoiceNumber !== currentInvoice.invoiceNumber &&
                    !(inv.id === currentInvoice.id && inv.status === 'concept')
                );

                const newSavedInvoices = [...filteredInvoices, currentInvoice];

                // Update local state immediately
                const newState = {
                    currentInvoice,
                    savedInvoices: newSavedInvoices,
                    editingSection: null
                }

                // Update localStorage for anonymous users
                localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(newSavedInvoices))

                // Then trigger the API call only if user is logged in
                if (useAuthStore.getState().isAuthenticated) {
                    invoiceApi.create(currentInvoice)
                        .then(cloudInvoice => {
                            // Update local state with cloud ID and ensure synced status
                            set(state => {
                                const updatedInvoices = state.savedInvoices.map(inv =>
                                    inv.invoiceNumber === cloudInvoice.invoiceNumber
                                        ? { ...cloudInvoice, synced: true }
                                        : inv
                                )

                                const updatedCurrentInvoice =
                                    state.currentInvoice.invoiceNumber === cloudInvoice.invoiceNumber
                                        ? { ...cloudInvoice, synced: true }
                                        : state.currentInvoice

                                // Update localStorage with new IDs
                                localStorage.setItem('dutchinvoice_local_invoices', JSON.stringify(updatedInvoices))

                                // Now that the invoice is saved, create the schedule if we have scheduleData
                                if (scheduleData && cloudInvoice.id) {
                                    // Use the schedule store directly to avoid circular dependencies
                                    import('../scheduleStore').then(({ useScheduleStore }) => {
                                        const { createSchedule } = useScheduleStore.getState();

                                        // Create the schedule with the saved invoice ID
                                        const scheduleToCreate = {
                                            ...scheduleData,
                                            templateInvoiceId: cloudInvoice.id
                                        };

                                        createSchedule(scheduleToCreate)
                                            .then(createdSchedule => {
                                                // Update the invoice with the scheduleId
                                                invoiceApi.update({
                                                    ...cloudInvoice,
                                                    scheduleId: createdSchedule.id
                                                }).catch(error => {
                                                    console.error('Failed to update invoice with scheduleId:', error);
                                                });

                                                // Also update local state
                                                set(state => ({
                                                    savedInvoices: state.savedInvoices.map(inv =>
                                                        inv.id === cloudInvoice.id
                                                            ? { ...inv, scheduleId: createdSchedule.id }
                                                            : inv
                                                    ),
                                                    currentInvoice: state.currentInvoice.id === cloudInvoice.id
                                                        ? { ...state.currentInvoice, scheduleId: createdSchedule.id }
                                                        : state.currentInvoice
                                                }));
                                            })
                                            .catch(error => {
                                                console.error('Failed to create schedule after invoice save:', error);
                                            });
                                    }).catch(error => {
                                        console.error('Failed to import schedule store:', error);
                                    });
                                }

                                return {
                                    savedInvoices: updatedInvoices,
                                    currentInvoice: updatedCurrentInvoice
                                }
                            })
                        })
                        .catch(error => {
                            console.error('Failed to save invoice:', error)
                            // If API call fails, mark as unsynced
                            set(state => ({
                                ...state,
                                savedInvoices: state.savedInvoices.map(inv =>
                                    inv.id === currentInvoice.id ? { ...inv, synced: false } : inv
                                )
                            }))
                        })
                }

                return newState
            } catch (error) {
                console.error('Failed to save invoice:', error)
                return state
            }
        }),

    resetCurrentInvoice: () =>
        set(() => {
            // First refresh settings from the backend
            const { refreshSettings } = get();
            refreshSettings(); // Refresh in background without awaiting

            // Create a new invoice with current settings
            const newCurrentInvoice = get().newInvoice();

            return {
                currentInvoice: newCurrentInvoice,
                editingSection: null,
                ...calculateInvoiceTotals(newCurrentInvoice.items, newCurrentInvoice.discount)
            }
        }),

    loadInvoice: (invoiceNumber) =>
        set((state) => {
            try {
                // First try to find invoice in local state
                const invoice = state.savedInvoices.find(inv => inv.invoiceNumber === invoiceNumber)
                if (!invoice) {
                    console.error('Invoice not found:', invoiceNumber)
                    return state
                }

                // Trigger background sync to get latest data (non-blocking)
                if (useAuthStore.getState().isAuthenticated) {
                    get().syncWithCloud().catch(console.error);
                }

                // Keep dates in YYYY-MM-DD format
                return {
                    currentInvoice: invoice,
                    editingSection: null,
                    ...calculateInvoiceTotals(invoice.items, invoice.discount)
                }
            } catch (error) {
                console.error('Failed to load invoice:', error)
                return state
            }
        }),

    deleteInvoice: (invoiceNumber) =>
        set((state) => {
            try {
                // Check if we're trying to delete the current invoice and it's a concept
                const isCurrentInvoice = state.currentInvoice.id === invoiceNumber || state.currentInvoice.invoiceNumber === invoiceNumber;
                const isCurrentConcept = isCurrentInvoice && !state.currentInvoice.invoiceNumber;

                // For concept invoices that are the current invoice, just reset
                if (isCurrentConcept) {
                    const newCurrentInvoice = get().newInvoice();
                    return {
                        currentInvoice: newCurrentInvoice,
                        editingSection: null,
                        ...calculateInvoiceTotals(newCurrentInvoice.items, newCurrentInvoice.discount)
                    }
                }

                // For saved invoices, find the invoice to delete
                const invoice = state.savedInvoices.find(inv =>
                    inv.invoiceNumber === invoiceNumber || inv.id === invoiceNumber
                )
                if (!invoice) {
                    console.error('Invoice not found:', invoiceNumber)
                    return state
                }

                const isDeletingCurrent = state.currentInvoice.invoiceNumber === invoiceNumber

                // Update local state immediately
                const newState = {
                    savedInvoices: state.savedInvoices.filter(inv => inv.invoiceNumber !== invoiceNumber),
                    ...(isDeletingCurrent && {
                        currentInvoice: get().newInvoice(),
                        editingSection: null,
                        ...calculateInvoiceTotals(sampleInvoice.items, sampleInvoice.discount)
                    })
                }

                // Then trigger the API call only if user is logged in
                if (useAuthStore.getState().isAuthenticated) {
                    invoiceApi.delete(invoice.id).catch(error => {
                        console.error('Failed to delete invoice from cloud:', error)
                    })
                }

                return newState
            } catch (error) {
                console.error('Failed to delete invoice:', error)
                return state
            }
        }),

    copyInvoice: (invoiceNumber) =>
        set((state) => {
            const invoice = state.savedInvoices.find(inv => inv.invoiceNumber === invoiceNumber)
            if (!invoice) return state

            // Generate a new ID for the copied invoice to avoid confusion
            const newId = crypto.randomUUID();

            // Create a new invoice based on the existing one
            const copiedInvoice = {
                ...get().newInvoice(invoice.sender),
                id: newId, // Explicitly set a new ID
                recipient: {
                    ...invoice.recipient,
                    isPlaceholder: { ...invoice.recipient.isPlaceholder }
                },
                companyId: invoice.companyId,
                paymentMethods: invoice.paymentMethods,
                items: invoice.items,
                invoiceNumber: '', // Empty invoice number for draft
                paymentTerms: invoice.paymentTerms,
                isPlaceholder: invoice.isPlaceholder,
                notes: invoice.notes,
                discount: invoice.discount,
                emailMessage: invoice.emailMessage,
                status: 'concept' as const
            }

            // Add the copied invoice to savedInvoices if it has content
            const hasContent = invoiceHasContent(copiedInvoice);

            // Only add to savedInvoices if it has content and is not already there
            const newSavedInvoices = hasContent
                ? [copiedInvoice, ...state.savedInvoices]
                : state.savedInvoices;

            return {
                currentInvoice: copiedInvoice,
                savedInvoices: newSavedInvoices,
                editingSection: null,
                ...calculateInvoiceTotals(copiedInvoice.items, copiedInvoice.discount)
            }
        }),

    getNextInvoiceNumber: () => {
        const state = get();
        return generateNextInvoiceNumber(state.settings)
    },

    isCurrentCompanySaved: () => {
        const { currentInvoice, settings } = get()
        if (!settings.defaultCompany) return false

        // Remove isPlaceholder from comparison
        const currentSender = { ...currentInvoice.sender }
        delete currentSender.isPlaceholder

        const savedCompany = { ...settings.defaultCompany }
        delete savedCompany.isPlaceholder

        // Use the isSameCompany function from invoiceCalculations
        return isSameCompany(currentSender, savedCompany)
    },

    saveAsDefaultCompany: () => {
        const { currentInvoice, settings, updateSettings } = get()
        const updatedSettings = {
            ...settings,
            defaultCompany: {
                ...currentInvoice.sender,
                isPlaceholder: undefined
            }
        }

        // Use updateSettings to handle both local and cloud sync
        updateSettings(updatedSettings)
    },
}) 